
import React, { useState } from 'react';
import logo from "../assets/logo_savatevent.jpg";
import { supabase } from '../services/supabaseClient';
import { Mail, Lock, Phone, MapPin, User, ArrowLeft, Loader2, Home } from 'lucide-react';
import Swal from 'sweetalert2';
import { translateError } from '../utils/errorTranslator';

export default function AuthPage({ onBack }) {
    const [mode, setMode] = useState('login'); // 'login' or 'signup'
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        phone: '',
        address: '',
        zipCode: '',
        city: ''
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleAuth = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (mode === 'signup') {
                if (formData.password !== formData.confirmPassword) {
                    throw new Error("Les mots de passe ne correspondent pas.");
                }
                if (!formData.phone || !formData.address || !formData.firstName || !formData.lastName || !formData.zipCode || !formData.city) {
                    throw new Error("Tous les champs sont obligatoires.");
                }

                const { error } = await supabase.auth.signUp({
                    email: formData.email,
                    password: formData.password,
                    options: {
                        data: {
                            first_name: formData.firstName,
                            last_name: formData.lastName,
                            phone: formData.phone,
                            address: formData.address,
                            zip_code: formData.zipCode,
                            city: formData.city
                        }
                    }
                });
                if (error) throw error;

                // 3. Notifier l'admin de la création
                fetch('https://icaqghpgcoapikttehjg.supabase.co/functions/v1/notify-new-user', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'apikey': 'sb_publishable_7nFecSfzP1uxlrIRKOwSJA_zIf8VfgR'
                    },
                    body: JSON.stringify({
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        email: formData.email,
                        phone: formData.phone
                    })
                }).catch(err => console.error("Erreur notification admin:", err));

                Swal.fire({
                    icon: 'success',
                    title: 'Inscription réussie',
                    text: 'Veuillez vérifier votre boîte mail pour confirmer votre compte.',
                });
            } else {
                const { error } = await supabase.auth.signInWithPassword({
                    email: formData.email,
                    password: formData.password,
                });
                if (error) throw error;
            }
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Erreur', text: translateError(err) });
        } finally {
            setLoading(false);
        }
    };

    const containerStyle = {
        maxWidth: '480px', 
        width: '90%',
        margin: '40px auto', 
        padding: '30px', 
        backgroundColor: 'transparent', // Suppression du fond blanc de l'encadrement
        borderRadius: '16px', 
        // boxShadow supprimé pour plus de légèreté si fond transparent
        fontFamily: 'sans-serif',
        boxSizing: 'border-box'
    };

    const inputContainerStyle = { position: 'relative', width: '100%', boxSizing: 'border-box' };

    const inputStyle = {
        width: '100%', 
        padding: '12px 40px', 
        borderRadius: '8px', 
        border: '1px solid #e2e8f0',
        backgroundColor: 'white', // Assure que les champs restent blancs
        fontSize: '14px', 
        marginBottom: '15px', 
        outline: 'none',
        boxSizing: 'border-box'
    };

    const iconStyle = { position: 'absolute', left: '12px', top: '12px', color: '#a0aec0' };

    return (
        <div style={containerStyle}>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <img src={logo} alt="Logo" style={{ width: '120px', height: '120px', borderRadius: '20px', marginBottom: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', objectFit: 'contain', backgroundColor: 'white' }} />
                <h1 style={{ color: '#2c5282', fontSize: '28px', fontWeight: 'bold', margin: '0 0 10px 0' }}>SavatEvent</h1>
                <p style={{ color: '#718096' }}>{mode === 'login' ? 'Heureux de vous revoir !' : 'Créez votre compte organisateur'}</p>
            </div>

            <form onSubmit={handleAuth} style={{ width: '100%' }}>
                {mode === 'signup' && (
                    <>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', width: '100%' }}>
                            <div style={inputContainerStyle}>
                                <User size={18} style={iconStyle} />
                                <input name="firstName" placeholder="Prénom" style={inputStyle} onChange={handleChange} required />
                            </div>
                            <div style={inputContainerStyle}>
                                <User size={18} style={iconStyle} />
                                <input name="lastName" placeholder="Nom" style={inputStyle} onChange={handleChange} required />
                            </div>
                        </div>
                        <div style={inputContainerStyle}>
                            <Phone size={18} style={iconStyle} />
                            <input name="phone" placeholder="Numéro de téléphone" style={inputStyle} onChange={handleChange} required />
                        </div>
                        <div style={inputContainerStyle}>
                            <MapPin size={18} style={iconStyle} />
                            <input name="address" placeholder="Adresse postale (rue, numéro)" style={inputStyle} onChange={handleChange} required />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '10px', width: '100%' }}>
                            <div style={inputContainerStyle}>
                                <Home size={18} style={iconStyle} />
                                <input name="zipCode" placeholder="CP" style={inputStyle} onChange={handleChange} required />
                            </div>
                            <div style={inputContainerStyle}>
                                <Home size={18} style={iconStyle} />
                                <input name="city" placeholder="Ville" style={inputStyle} onChange={handleChange} required />
                            </div>
                        </div>
                    </>
                )}

                <div style={inputContainerStyle}>
                    <Mail size={18} style={iconStyle} />
                    <input name="email" type="email" placeholder="Adresse mail" style={inputStyle} onChange={handleChange} required />
                </div>

                <div style={inputContainerStyle}>
                    <Lock size={18} style={iconStyle} />
                    <input name="password" type="password" placeholder="Mot de passe" style={inputStyle} onChange={handleChange} required />
                </div>

                {mode === 'signup' && (
                    <div style={inputContainerStyle}>
                        <Lock size={18} style={iconStyle} />
                        <input name="confirmPassword" type="password" placeholder="Confirmez le mot de passe" style={inputStyle} onChange={handleChange} required />
                    </div>
                )}

                <button 
                    type="submit" 
                    disabled={loading}
                    style={{ width: '100%', padding: '12px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                >
                    {loading ? <Loader2 className="animate-spin" size={20} /> : (mode === 'login' ? 'Se connecter' : "S'inscrire")}
                </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <p style={{ fontSize: '14px', color: '#4a5568' }}>
                    {mode === 'login' ? "Pas encore de compte ?" : "Déjà un compte ?"}
                    <button 
                        onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
                        style={{ marginLeft: '5px', background: 'none', border: 'none', color: '#3182ce', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                        {mode === 'login' ? "Inscrivez-vous" : "Connectez-vous"}
                    </button>
                </p>
            </div>

            <button 
                onClick={onBack}
                style={{ width: '100%', marginTop: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: 'none', border: 'none', color: '#a0aec0', cursor: 'pointer', fontSize: '14px' }}
            >
                <ArrowLeft size={16} /> Retour à l'accueil
            </button>
        </div>
    );
}
